import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { authService } from '../services/authService';
import { saveTask, removeTask, getTasksForComponent } from '../services/taskPersistence';
import { logUiEvent } from '../services/uiLogger';
import { transcribeService } from '../services/transcribeService';
import EditSpeakersModal from './EditSpeakersModal';
import { voiceService } from '../services/voiceService';
import { cleanSpeakerName } from './protocol/speakerLabels';
import VoiceIdBar from './VoiceIdBar';
import TranscriptView from './transcript/TranscriptView';
import { formatTimecodeHMMSS } from '../FormatTime';
import SettingsBar from './transcription/SettingsBar';
import ProcessingBar from './transcription/ProcessingBar';
import ResultToolbar from './transcription/ResultToolbar';
import { getAudioDuration } from '../utils/audioDuration';
import { buildDownloadName } from '../utils/downloadName';
import { fetchService } from '../services/fetchService';
import { extractAudio } from '../lib/audioExtract';
import { reportAudioCapabilities } from '../lib/audioExtract/probe';
import { NO_SPEECH_MESSAGE, statusHasNoSpeech, resultIsEmpty } from '../lib/noSpeech';
import { resolveQuotaBlockedError } from '../lib/quotaError';
import { ENDPOINTS } from '../config/endpoints';

const API_BASE_URL = ENDPOINTS.asr;
const API_BASE_URL_DIAR = ENDPOINTS.diarize;
const API_BASE_URL_FETCH = ENDPOINTS.fetch;
const API_BASE_URL_PARAKEET = ENDPOINTS.asrParakeet;


const TranscriptionWithoutDiarization = ({ state, setState, onOpenProtocol, onRenameSpeakers,
                                          onSpeakerConfirmed }) => {
  const [file, setFile] = useState(state.file || null);
  const [rawTranscription, setRawTranscription] = useState(state.rawTranscription || null);
  const [parsedTranscription, setParsedTranscription] = useState(state.parsedTranscription || []);
  const [isProcessing, setIsProcessing] = useState(state.isProcessing || false);
  const [progress, setProgress] = useState(state.progress || 0);
  const [uploadProgress, setUploadProgress] = useState(state.uploadProgress || 0);
  const [transcribeProgress, setTranscribeProgress] = useState(state.transcribeProgress || 0);
  const [currentBatch, setCurrentBatch] = useState(state.currentBatch || null);
  const [totalBatches, setTotalBatches] = useState(state.totalBatches || null);
  const [totalTranscriptionTime, setTotalTranscriptionTime] = useState(state.totalTranscriptionTime || null);
  const [taskId, setTaskId] = useState(state.taskId || null);
  const [error, setError] = useState(state.error || null);
  // Речи в файле не нашлось. Отдельно от error: это не отказ сервиса, и красным
  // его показывать нечестно — но и молча отдавать пустой экран нельзя.
  const [noSpeech, setNoSpeech] = useState(false);
  const [addDiarization, setAddDiarization] = useState(state.addDiarization || false);
  const [numSpeakers, setNumSpeakers] = useState(state.numSpeakers || null);
  // Вилка «от — до» для случая, когда точное число неизвестно: без неё модель
  // ищет от одного участника до бесконечности и на длинных записях ошибается.
  const [minSpeakers, setMinSpeakers] = useState(state.minSpeakers || null);
  const [maxSpeakers, setMaxSpeakers] = useState(state.maxSpeakers || null);
  const [url, setUrl] = useState(state.url || '');
  const [taskBaseUrl, setTaskBaseUrl] = useState(state.taskBaseUrl || null);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [deleteTimecodes, setDeleteTimecodes] = useState(state.deleteTimecodes || false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [speakers, setSpeakers] = useState([]);
  // Узнавание участников по голосу: доступно ли аккаунту, метки узнанных
  // спикеров и обратное соответствие «показанное имя → SPEAKER_N».
  // Имена держим в ref: get_status отдаёт их в том же тике, в котором мы уже
  // идём за результатом, — state там ещё не обновится.
  const [voiceIdEnabled, setVoiceIdEnabled] = useState(false);
  const [speakerIdByLabel, setSpeakerIdByLabel] = useState(state.speakerIdByLabel || {});
  const [voiceTaskId, setVoiceTaskId] = useState(state.voiceTaskId || null);
  const speakerNamesRef = useRef({});
  // Узнанные по голосу — для полосы подтверждения над расшифровкой.
  const [recognizedSpeakers, setRecognizedSpeakers] = useState(state.recognizedSpeakers || []);
  // Почти-совпадения: имя предлагается вопросом, в текст само не попадает.
  const [suggestedSpeakers, setSuggestedSpeakers] = useState(state.suggestedSpeakers || []);
  const speakerSuggestionsRef = useRef({});
  const voiceStatusAskedRef = useRef(false);
  const [currentProcessType, setCurrentProcessType] = useState('транскрибация');
  const [queuePosition, setQueuePosition] = useState(0);
  const [activeFileName, setActiveFileName] = useState(state.activeFileName || null);
  const [taskStartedAt, setTaskStartedAt] = useState(state.taskStartedAt || null);
  const [etaSeconds, setEtaSeconds] = useState(null);
  const [audioDuration, setAudioDuration] = useState(state.audioDuration || null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractProgress, setExtractProgress] = useState(0);

  const fileInputRef = useRef(null);
  const urlAbortRef = useRef(false);
  const extractAbortRef = useRef(null);
  const autoProtocolRef = useRef(false);  // авто-открыть «Протокол» по завершении свежей транскрибации

  useEffect(() => {
    const saved = getTasksForComponent('transcription');
    if (!saved.length) {
      // Зомби-состояние: isProcessing без задач
      if (isProcessing) {
        setIsProcessing(false);
        setProgress(0);
        setTranscribeProgress(0);
      }
      return;
    }
    const task = saved[0];

    // Обычная задача
    (async () => {
      try {
        const resp = await axios.get(task.statusEndpoint, { headers: authService.getAuthHeaders() });
        const { status } = resp.data;
        if (status === 'READY') {
          // Восстановление после перезагрузки страницы: узнанные имена берём
          // из того же ответа статуса, иначе они потерялись бы вместе с вкладкой.
          speakerNamesRef.current = resp.data.speaker_names || {};
          speakerSuggestionsRef.current = resp.data.speaker_suggestions || {};
          await fetchTranscriptionResult(task.taskId, task.isDiarization, task.baseUrl || null);
          removeTask(task.taskId);
        } else if (status === 'ERROR') {
          setError('Задача завершилась с ошибкой');
          removeTask(task.taskId);
        } else {
          // CREATED / UPLOADING / PENDING / PROCESSING — возобновляем поллинг
          setAddDiarization(task.isDiarization);
          setTaskId(task.taskId);
          setTaskBaseUrl(task.baseUrl || null);
          setActiveFileName(task.fileName || null);
          setTaskStartedAt(task.startedAt || null);
          setIsProcessing(true);
        }
      } catch {
        removeTask(task.taskId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setState({
      file,
      rawTranscription,
      parsedTranscription,
      isProcessing,
      progress,
      uploadProgress,
      transcribeProgress,
      currentBatch,
      totalBatches,
      totalTranscriptionTime,
      taskId,
      error,
      addDiarization,
      numSpeakers,
      minSpeakers,
      maxSpeakers,
      deleteTimecodes,
      speakers,
      activeFileName,
      taskStartedAt,
      audioDuration,
      url,
      taskBaseUrl,
      voiceTaskId,
      speakerIdByLabel,
      recognizedSpeakers,
      suggestedSpeakers
    });
  }, [file, rawTranscription, parsedTranscription, isProcessing, progress, uploadProgress,
      transcribeProgress, currentBatch, totalBatches,
      totalTranscriptionTime, taskId, error,
      addDiarization, numSpeakers, minSpeakers, maxSpeakers, deleteTimecodes, speakers,
      activeFileName, taskStartedAt, audioDuration, url, taskBaseUrl,
      voiceTaskId, speakerIdByLabel, recognizedSpeakers, suggestedSpeakers, setState]);

  useEffect(() => {
    if (!file) { setAudioDuration(null); return; }
    getAudioDuration(file).then(setAudioDuration);
  }, [file]);

  const formatTimecode = (timecodeStr) => {
    const matches = timecodeStr.match(/\[(\d{2}):(\d{2}):(\d{2}\.\d{3}) - (\d{2}):(\d{2}):(\d{2}\.\d{3})\]/);
    if (!matches) return timecodeStr;

    const [_, h1, m1, s1, h2, m2, s2] = matches;
    const startSeconds = parseFloat(h1) * 3600 + parseFloat(m1) * 60 + parseFloat(s1);
    const endSeconds = parseFloat(h2) * 3600 + parseFloat(m2) * 60 + parseFloat(s2);

    return `[${formatTimecodeHMMSS(startSeconds)} - ${formatTimecodeHMMSS(endSeconds)}]`;
  };

const parseTranscription = (text) => {
  if (!text) return [];

  const fragments = text.split('\n').filter(line => line.trim());
  return fragments.map(fragment => {
    const timecodeMatch = fragment.match(/\[\d{2}:\d{2}:\d{2}\.\d{3} - \d{2}:\d{2}:\d{2}\.\d{3}\]/);
    // Изменение: заменяем SPEAKER на УЧАСТНИК
    const speakerMatch = fragment.match(/\[SPEAKER_\d+\]/);

    // Если включена опция удаления таймкодов, не добавляем их
    const timecode = deleteTimecodes ? null : (timecodeMatch ? formatTimecode(timecodeMatch[0]) : null);

    let speaker = null;
    if (speakerMatch) {
      // Изменение: преобразование SPEAKER_X в УЧАСТНИК_Y, где Y = X + 1
      const speakerNumber = parseInt(speakerMatch[0].match(/\d+/)[0]);
      speaker = `[УЧАСТНИК_${speakerNumber + 1}]`;
    }

    const text = fragment
      .replace(timecodeMatch?.[0] || '', '')
      .replace(speakerMatch?.[0] || '', '')
      .replace(':', '')
      .trim();

    return { timecode, speaker, text };
  });
};


const formatTranscriptionText = (fragments) => {
  if (!fragments?.length) return '';

  return fragments.map(fragment => {
    const { timecode, speaker, text } = fragment;

    // Если нет таймкода (или они отключены) - возвращаем просто текст
    if ((!timecode || deleteTimecodes) && (!speaker || !addDiarization)) return text;

    const metadata = addDiarization && speaker
      ? `<span class="font-bold text-paper-accent">${speaker}</span> ${!deleteTimecodes && timecode ? `<span class="text-paper-hint">${timecode}</span>` : ''}:`
      : !deleteTimecodes && timecode ? `<span class="text-paper-hint">${timecode}</span>:` : '';

    return `<div class="mt-px"><span class="text-sm">${metadata}</span>\n${text}</div>`;
  }).join('');
};

  const extractSpeakers = (fragments) => {
    if (!fragments?.length) return [];
    return [...new Set(fragments
      .map(fragment => fragment.speaker)
      .filter(speaker => speaker)
    )];
  };

  const handleEditSpeakerNames = () => {
    const extractedSpeakers = extractSpeakers(parsedTranscription);
    setSpeakers(extractedSpeakers);
    // Доступность узнавания голоса спрашиваем лениво и один раз за сеанс — при
    // открытии окна переименования. Раньше вопрос был привязан ещё и к наличию
    // свежей задачи, и человек не понимал, почему галочки нет: теперь она видна
    // всегда, а неготовность записи объясняется текстом.
    if (!voiceStatusAskedRef.current) {
      voiceStatusAskedRef.current = true;
      voiceService.status().then(setVoiceIdEnabled);
    }
    setIsEditModalOpen(true);
  };

  const handleSaveSpeakerNames = (editedSpeakers, remembered = []) => {
    if (remembered.length && voiceTaskId) {
      const assignments = {};
      const unmapped = [];
      remembered.forEach((label) => {
        const speakerId = speakerIdByLabel[label];
        const name = cleanSpeakerName(editedSpeakers[label]);
        if (speakerId && name) assignments[speakerId] = name;
        else unmapped.push(label);
      });
      if (unmapped.length) {
        // Молча терять «запомнить голос» нельзя: человек уверен, что сохранил.
        setError('Не удалось запомнить голос: ' + unmapped.join(', '));
      }
      if (Object.keys(assignments).length) {
        voiceService.remember(voiceTaskId, assignments).then((result) => {
          const skipped = [
            ...(result?.skipped_short || []).map((n) => `${n} — мало речи в этой записи`),
            ...(result?.skipped_mismatch || []).map((n) => `${n} — голос не похож на сохранённый под этим именем`),
            ...(result?.skipped_unavailable || []).map((n) => `${n} — данные этой записи уже недоступны`),
          ];
          // Частичный успех — тоже повод сказать: раньше сохранение одного из
          // двух выглядело как полный успех, и вторая галочка терялась молча.
          if (skipped.length) {
            setError('Не запомнили: ' + skipped.join('; ') + '.');
          } else if (!result?.saved) {
            setError('Голос не запомнен: данные этой записи уже недоступны '
              + '(отпечатки ждут решения 7 дней).');
          }
        }).catch((err) => {
          setError('Не удалось запомнить голос: ' + (err.response?.data?.detail || err.message));
        });
      }
    }
    if (parsedTranscription.length) {
      const updatedTranscription = parsedTranscription.map(fragment => ({
        ...fragment,
        speaker: fragment.speaker ? editedSpeakers[fragment.speaker] || fragment.speaker : null
      }));

      // Пересобираем rawTranscription (для сохранения и копирования)
      const rebuiltRaw = updatedTranscription.map(f => {
        const parts = [];
        if (f.speaker) parts.push(f.speaker);
        if (f.timecode) parts.push(f.timecode);
        const prefix = parts.length ? parts.join(' ') + ':' : '';
        return `${prefix} ${f.text || ''}`.trim();
      }).join('\n');

      setRawTranscription(rebuiltRaw);

      setParsedTranscription(updatedTranscription);
      // Соответствие «метка на экране → метка диаризации» переносим на новые
      // имена: без этого второй заход в окно переименования уже не нашёл бы
      // спикера и «запомнить голос» тихо ничего бы не сохранил.
      // Тот же map — в состояние протокола: там лежит своя копия текста, и без
      // этого человек, вернувшись на другую вкладку, видел старые имена.
      if (onRenameSpeakers) onRenameSpeakers(editedSpeakers, 'transcription');
      setRecognizedSpeakers((prev) => prev.map((item) => {
        const renamed = (editedSpeakers[item.label] || '').trim();
        return renamed ? { ...item, label: renamed, name: cleanSpeakerName(renamed) } : item;
      }));
      setSpeakerIdByLabel((prev) => {
        const next = {};
        Object.entries(prev).forEach(([label, speakerId]) => {
          const renamed = (editedSpeakers[label] || '').trim();
          next[renamed || label] = speakerId;
        });
        return next;
      });
    }
  };

  const cleanTimecodes = (fragments) => {
    if (!fragments?.length) return '';
    return fragments.map(fragment =>
      fragment.speaker ? `${fragment.speaker}: ${fragment.text}` : fragment.text
    ).join('\n');
  };

  const handleOpenProtocol = () => {
    const cleanedText = cleanTimecodes(parsedTranscription);
    // Пятым аргументом — карта «метка на экране → метка диаризации»: в Протоколе
    // тоже переименовывают участников, и «запомнить голос» должно работать там.
    onOpenProtocol(cleanedText, rawTranscription, activeFileName || file?.name || '',
                   voiceTaskId || taskId, speakerIdByLabel, recognizedSpeakers);
  };

  // После завершения свежей транскрибации (не resume) —
  // автоматически открываем таб «Протокол» с расшифровкой.
  useEffect(() => {
    if (autoProtocolRef.current && parsedTranscription.length > 0) {
      autoProtocolRef.current = false;
      handleOpenProtocol();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parsedTranscription]);

  const checkTaskStatus = useCallback(async () => {
    if (!taskId || !isProcessing) return;

    try {
      const baseUrl = taskBaseUrl || (addDiarization ? API_BASE_URL_DIAR : API_BASE_URL);
      const response = await axios.get(`${baseUrl}/get_status?task_id=${taskId}`, {
        headers: authService.getAuthHeaders()
      });

      const {
        status,
        progress,
        upload_progress,
        transcribe_progress,
        current_batch,
        total_batches,
        total_transcription_time,
        queue_position,
        error: taskError
      } = response.data;

      setProgress(progress);
      setUploadProgress(upload_progress);
      setTranscribeProgress(transcribe_progress);
      setCurrentBatch(current_batch);
      setTotalBatches(total_batches);
      setQueuePosition(queue_position || 0);

      // Определяем стадию по типу сервиса и прогрессу
      if (response.data.stage === 'loading_model') {
        setCurrentProcessType('загрузка модели');
      } else if (addDiarization && transcribe_progress > 0 && transcribe_progress <= 20) {
        setCurrentProcessType('разделение спикеров');
      } else {
        setCurrentProcessType('транскрибация');
      }

      if (transcribe_progress > 5 && taskStartedAt) {
        const elapsed = (Date.now() - new Date(taskStartedAt).getTime()) / 1000;
        const remaining = Math.round(elapsed / transcribe_progress * (100 - transcribe_progress));
        setEtaSeconds(remaining > 0 ? remaining : null);
      }

      if (status === 'READY') {
        setTotalTranscriptionTime(total_transcription_time);
        setIsProcessing(false);
        if (statusHasNoSpeech(response.data)) setNoSpeech(true);
        speakerNamesRef.current = response.data.speaker_names || {};
        speakerSuggestionsRef.current = response.data.speaker_suggestions || {};
        await fetchTranscriptionResult();
        removeTask(taskId);
      } else if (status === 'ERROR') {
        setError('Ошибка обработки: ' + taskError);
        setIsProcessing(false);
        removeTask(taskId);
      }
    } catch (err) {
      console.error('Error checking task status:', err);
      setError('Ошибка получения статуса: ' + (err.response?.data?.detail || err.message));
      setIsProcessing(false);
      if (taskId) removeTask(taskId);
    }
  }, [taskId, isProcessing, addDiarization, taskBaseUrl]);

  useEffect(() => {
    let intervalId;
    if (isProcessing && taskId) {
      intervalId = setInterval(checkTaskStatus, 1000);
    }
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isProcessing, taskId, checkTaskStatus]);

  // Уход с вкладки размонтирует компонент (App.js рендерит его по условию) —
  // без этого воркер продолжит молотить CPU, а отменить его будет нечем.
  useEffect(() => () => extractAbortRef.current?.abort(), []);

  // Проба возможностей браузера — без гейта по роли и без флага, нужна раскладка
  // по всем браузерам (в т.ч. тем, где извлечение ещё ни разу не запускалось).
  // Ничего не извлекает, сама разбирается с однократностью отправки за сессию.
  useEffect(() => { reportAudioCapabilities(); }, []);

  /**
   * Общий путь для обеих точек входа файла — выбора через диалог и броска
   * мышью. Извлечение звука сюда не входит — это первая стадия обработки,
   * она стартует по кнопке «Начать» в handleUpload.
   */
  const acceptFile = (selectedFile) => {
    if (!selectedFile) return;
    setFile(selectedFile);
    resetState();
  };

  const handleFileChange = (event) => acceptFile(event.target.files[0]);

  const resetState = () => {
    setTaskId(null);
    setTaskBaseUrl(null);
    setRawTranscription(null);
    setParsedTranscription([]);
    setError(null);
    setNoSpeech(false);
    setProgress(0);
    setUploadProgress(0);
    setDownloadProgress(0);
    setTranscribeProgress(0);
    setCurrentBatch(null);
    setTotalBatches(null);
    setTotalTranscriptionTime(null);
    setCurrentProcessType('транскрибация');
    setQueuePosition(0);
    setActiveFileName(null);
    setTaskStartedAt(null);
    setEtaSeconds(null);
  };

  const handleCancel = async () => {
    logUiEvent('cancel', { component: 'transcription', task_id: taskId });

    // Во время извлечения задачи на сервере ещё нет — отменять нечего,
    // достаточно прервать сам конвейер извлечения.
    if (isExtracting) {
      extractAbortRef.current?.abort();
    } else {
      const currentBase = taskBaseUrl || (addDiarization ? API_BASE_URL_DIAR : API_BASE_URL);
      await transcribeService.cancelTask(currentBase, taskId);

      if (taskId) removeTask(taskId);
    }

    urlAbortRef.current = true;
    setIsProcessing(false);
    resetState();
  };

const handleStart = () => {
  if (url && url.trim()) {
    handleUrlSubmit();
  } else {
    handleUpload();
  }
};

const handleUrlSubmit = async () => {
  resetState();
  urlAbortRef.current = false;
  autoProtocolRef.current = true;
  setAddDiarization(false);
  setIsProcessing(true);
  setCurrentProcessType('скачивание ссылки');

  try {
    const token = authService.getToken();
    const { fetch_id, title, duration } = await fetchService.submitUrl(
      API_BASE_URL_FETCH, token, url.trim());
    setActiveFileName(title || 'Запись по ссылке');
    if (duration) setAudioDuration(duration);

    const parakeetTaskId = await fetchService.pollFetch(
      API_BASE_URL_FETCH, token, fetch_id,
      { intervalMs: 3000, onProgress: (p) => setDownloadProgress(p) });

    if (urlAbortRef.current) return;

    setTaskId(parakeetTaskId);
    setTaskBaseUrl(API_BASE_URL_PARAKEET);
    setCurrentProcessType('транскрибация');
    const startedAt = new Date().toISOString();
    setTaskStartedAt(startedAt);
    saveTask({
      taskId: parakeetTaskId,
      service: 'bit-transcribe-parakeet',
      baseUrl: API_BASE_URL_PARAKEET,
      statusEndpoint: `${API_BASE_URL_PARAKEET}/get_status?task_id=${parakeetTaskId}`,
      resultEndpoint: `${API_BASE_URL_PARAKEET}/get_file?task_id=${parakeetTaskId}`,
      fileName: title || 'Запись по ссылке',
      startedAt,
      component: 'transcription',
      isDiarization: false,
    });
  } catch (err) {
    if (urlAbortRef.current) { setIsProcessing(false); return; }
    console.error('Error fetching by url:', err);
    setError('Ошибка обработки ссылки: ' + (err.response?.data?.detail || err.message));
    setIsProcessing(false);
  }
};

const handleUpload = async () => {
  if (!file) return;

  resetState();
  autoProtocolRef.current = true;
  setIsProcessing(true);

  // Извлечение звука — первая стадия обработки, до сборки FormData: на
  // сервер должна уехать дорожка вместо видеоряда (спека 2026-08-10).
  let uploadFile = file;

  // Раскатка процентная — bit-auth уже учёл глобальный флаг, роль admin,
  // личный opt-in и бакет по email, сюда приходит готовый булев. Фронт
  // свой гейт по роли больше не держит (см. docs/backlog.md, раздел про
  // извлечение звука).
  const tokenData = authService.getTokenData();
  if (tokenData?.audio_extract_enabled) {
    setCurrentProcessType('извлечение звука');
    extractAbortRef.current = new AbortController();
    setIsExtracting(true);
    setExtractProgress(0);
    try {
      const { file: extracted } = await extractAudio(uploadFile, {
        signal: extractAbortRef.current.signal,
        onProgress: (p) => setExtractProgress(Math.round(p * 100)),
      });
      if (extracted) {
        uploadFile = extracted;
        setFile(extracted);
      }
    } catch (err) {
      if (err.name === 'AudioTooLongError') {
        setError(err.message);
        setIsProcessing(false);
        return;
      }
      if (err.name === 'AbortError') {
        // Пользователь сам отменил извлечение — тихо выходим, без ошибки.
        setIsProcessing(false);
        return;
      }
      // Всё остальное — исключение ДО того, как extractAudio дошёл до своей
      // записи в audio_extract (воркер не поднялся, упал динамический import
      // и т.п.). Без этой записи о случае вообще не узнать: ровно вчерашний
      // сценарий, из-за которого фича не работала и никто не заметил.
      console.error(
        'Извлечение звука упало вне конвейера — загружается исходный файл целиком, '
        + 'экономия трафика потеряна:', err,
      );
      logUiEvent('audio_extract', {
        component: 'transcription',
        meta: {
          ok: false,
          path: 'fallback',
          reason: 'component_error',
          error_name: err.name || '',
          error_message: err.message || '',
          src_bytes: uploadFile.size,
        },
      });
    } finally {
      setIsExtracting(false);
      extractAbortRef.current = null;
    }
  }

  setCurrentProcessType('загрузка файла');

  const formData = new FormData();
  formData.append('file', uploadFile);

  try {
    const baseUrl = addDiarization ? API_BASE_URL_DIAR : API_BASE_URL;
    // Второй аргумент обязателен: baseUrl бывает относительным путём вида
    // /asr-api (same-origin), и new URL() без базы бросает TypeError ещё до
    // первого сетевого запроса.
    const url = new URL(`${baseUrl}/start_transcribing`, window.location.origin);
    url.searchParams.append('diarize', addDiarization);
    url.searchParams.append('remove_timestamps', deleteTimecodes);
    if (addDiarization && numSpeakers !== null) {
      url.searchParams.append('num_speakers', numSpeakers);
    }
    if (addDiarization && numSpeakers === null) {
      // Точное число вилку перебивает — так устроен сам конвейер, поэтому
      // отправляем её только когда точного нет.
      if (minSpeakers !== null) url.searchParams.append('min_speakers', minSpeakers);
      if (maxSpeakers !== null) url.searchParams.append('max_speakers', maxSpeakers);
    }

    // Загрузка живёт минуты и переживает access-токен, поэтому свой кред —
    // часовой токен назначения upload, а не Bearer (docs/superpowers/sdd/…).
    const uploadHeaders = await authService.getUploadHeaders();
    const uploadStartedAt = Date.now();
    const response = await axios.post(url.toString(), formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
        ...uploadHeaders,
      },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
        setUploadProgress(percentCompleted);
        setCurrentProcessType('загрузка файла');
      }
    });

    // Скорость канала — контрольная группа для решения по извлечению звука:
    // время извлечения зависит от длительности файла, экономия — от размера,
    // без скорости канала одно в другое не перевести (docs/backlog.md).
    // Пишем при любой загрузке, не только после извлечения — иначе не узнаем
    // скорость у тех, кто грузит аудио и extractAudio даже не вызывался.
    try {
      const uploadMs = Date.now() - uploadStartedAt;
      const uploadBytes = uploadFile.size;
      const wasExtracted = uploadFile !== file;
      logUiEvent('upload_speed', {
        component: 'transcription',
        meta: {
          bytes: uploadBytes,
          ms: uploadMs,
          mbps: uploadMs > 0 ? Math.round((uploadBytes * 8 / uploadMs / 1000) * 100) / 100 : 0,
          extracted: wasExtracted,
          src_bytes: wasExtracted ? file.size : uploadBytes,
        },
      });
    } catch (e) {
      // Метрика важна, но не важнее загрузки — не ломаем путь пользователя.
    }

    setTaskId(response.data.task_id);

    setCurrentProcessType('транскрибация');
    setActiveFileName(uploadFile.name);
    const startedAt = new Date().toISOString();
    setTaskStartedAt(startedAt);
    const _baseUrlForTask = addDiarization ? API_BASE_URL_DIAR : API_BASE_URL;
    saveTask({
      taskId: response.data.task_id,
      service: addDiarization ? 'bit-diarize-v3' : 'bit-transcribe-v3',
      statusEndpoint: `${_baseUrlForTask}/get_status?task_id=${response.data.task_id}`,
      resultEndpoint: `${_baseUrlForTask}/get_file?task_id=${response.data.task_id}`,
      fileName: uploadFile?.name || '',
      startedAt: new Date().toISOString(),
      component: 'transcription',
      isDiarization: addDiarization,
    });

  } catch (err) {
    console.error('Error uploading file:', err);
    const blocked = await resolveQuotaBlockedError(err);
    setError(blocked
      ? `${blocked}. Продлить или сменить тариф — в личном кабинете, кнопка «Сменить тариф».`
      : 'Ошибка загрузки файла: ' + (err.response?.data?.detail || err.message));
    setIsProcessing(false);
  }
};

const fetchTranscriptionResult = async (overrideTaskId = null, overrideIsDiarization = null, overrideBaseUrl = null) => {
  const tid = overrideTaskId ?? taskId;
  const isDiar = overrideIsDiarization ?? addDiarization;
  if (!tid) return;

  try {
    const baseUrl = overrideBaseUrl ?? taskBaseUrl ?? (isDiar ? API_BASE_URL_DIAR : API_BASE_URL);

    const response = await axios.get(`${baseUrl}/get_file?task_id=${tid}`, {
      headers: authService.getAuthHeaders(),
      responseType: 'json'
    });

    let fragments = [];

    if (isDiar && Array.isArray(response.data)) {
      const recognized = speakerNamesRef.current || {};
      const idByLabel = {};
      fragments = response.data.map(entry => {
        const speakerNumber = parseInt(entry.speaker?.split('_')[1]) ?? 0;
        // Узнали голос — подписываем именем, нет — обычный «УЧАСТНИК_N».
        const label = recognized[entry.speaker]
          ? `[${recognized[entry.speaker]}]`
          : `[УЧАСТНИК_${speakerNumber + 1}]`;
        if (entry.speaker) idByLabel[label] = entry.speaker;
        return {
          speaker: label,
          timecode: `[${formatTimecodeHMMSS(entry.start)} - ${formatTimecodeHMMSS(entry.end)}]`,
          text: entry.text.trim()
        };
      });
      // Метка на экране → метка диаризации: «запомнить голос» отправляет вторую,
      // потому что векторы в bit-history лежат под ней.
      setSpeakerIdByLabel(idByLabel);
      setVoiceTaskId(tid);
      setRecognizedSpeakers(Object.entries(recognized).map(([speakerId, name]) => ({
        speakerId, name, label: `[${name}]`,
      })));
      // Метка предложенного — «УЧАСТНИК_N», как он показан в тексте: имя ещё не
      // подставлено, человеку надо понять, о ком спрашивают.
      setSuggestedSpeakers(Object.entries(speakerSuggestionsRef.current || {}).map(
        ([speakerId, item]) => ({
          speakerId,
          name: item.name,
          score: item.score,
          label: `[УЧАСТНИК_${(parseInt(speakerId.split('_')[1], 10) || 0) + 1}]`,
        })));
      setRawTranscription(fragments.map(f => `${f.speaker} ${f.timecode}: ${f.text}`).join('\n'));
    } else {
      const text = response.data;
      fragments = parseTranscription(text);
      setRawTranscription(text);
    }

    // Движки, не знающие про no_speech (диаризация, parakeet, стерео-АТС), отдают
    // при молчании пустой результат — тогда судим по нему. resultIsEmpty понимает
    // оба вида ответа: строку и список фрагментов диаризации.
    if (resultIsEmpty(response.data)) setNoSpeech(true);

    setParsedTranscription(fragments);

  } catch (err) {
    console.error('Error fetching transcription result:', err);
    setError('Ошибка получения результата: ' + (err.response?.data?.detail || err.message));
  }
};

  const handleCopy = () => {
    if (rawTranscription) {
      navigator.clipboard.writeText(rawTranscription);
    }
  };

  const handleSave = () => {
    if (!rawTranscription) {
      return;
    }

    const fileName = buildDownloadName(activeFileName || file?.name, 'trnscr', 'txt');

    const element = document.createElement("a");
    const blob = new Blob([rawTranscription], { type: 'text/plain' });
    element.href = URL.createObjectURL(blob);
    element.download = fileName;
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

const handleDelete = () => {
    setFile(null);
    resetState();
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleFileClear = () => {
    setFile(null);
    setAudioDuration(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleNewTranscription = () => {
    logUiEvent('new', { component: 'transcription' });
    handleDelete();
    handleFileClear();
    setUrl('');
  };

  const handleGlobalDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isProcessing && !isExtracting && !parsedTranscription.length) setIsDragOver(true);
  };

  const handleGlobalDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleGlobalDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    if (!isProcessing && !isExtracting && !parsedTranscription.length && e.dataTransfer.files.length > 0) {
      setError(null);
      acceptFile(e.dataTransfer.files[0]);
    }
  };

  return (
    <div
      className={`flex flex-col h-full font-montserrat ${isDragOver ? 'ring-2 ring-paper-accent ring-inset' : ''}`}
      onDragOver={handleGlobalDragOver}
      onDragLeave={handleGlobalDragLeave}
      onDrop={handleGlobalDrop}
    >
      <input
        type="file"
        onChange={handleFileChange}
        className="hidden"
        ref={fileInputRef}
        accept="audio/*,video/*,.aac,.mkv"
      />

      {isProcessing ? (
        <ProcessingBar
          fileName={activeFileName || file?.name || 'файл'}
          addDiarization={addDiarization}
          numSpeakers={numSpeakers}
          stage={currentProcessType}
          progress={currentProcessType === 'загрузка файла' ? uploadProgress : currentProcessType === 'извлечение звука' ? extractProgress : currentProcessType === 'скачивание ссылки' ? downloadProgress : transcribeProgress}
          etaSeconds={etaSeconds}
          queuePosition={queuePosition}
          onCancel={handleCancel}
        />
      ) : parsedTranscription.length > 0 ? (
        <ResultToolbar
          fileName={activeFileName || file?.name || 'файл'}
          audioDuration={audioDuration}
          speakersCount={addDiarization ? new Set(parsedTranscription.map(s => s.speaker).filter(Boolean)).size : 0}
          showTimecodes={deleteTimecodes}
          addDiarization={addDiarization}
          onToggleTimecodes={setDeleteTimecodes}
          onEditSpeakers={handleEditSpeakerNames}
          onCopy={handleCopy}
          onDownload={handleSave}
          onNewTranscription={handleNewTranscription}
          onOpenProtocol={handleOpenProtocol}
        />
      ) : (
        <SettingsBar
          file={file}
          audioDuration={audioDuration}
          addDiarization={addDiarization}
          numSpeakers={numSpeakers}
          minSpeakers={minSpeakers}
          maxSpeakers={maxSpeakers}
          onSpeakerRangeChange={(min, max) => { setMinSpeakers(min); setMaxSpeakers(max); }}
          isProcessing={isProcessing || isExtracting}
          onFileClick={() => fileInputRef.current?.click()}
          onFileClear={handleFileClear}
          onDiarizationChange={setAddDiarization}
          onNumSpeakersChange={setNumSpeakers}
          onStart={handleStart}
          url={url}
          onUrlChange={(v) => { setUrl(v); setError(null); }}
        />
      )}

      {error && (
        <div className="px-4 py-2 text-sm text-paper-accent bg-paper-accent/[0.06] border-b ">
          {error}
        </div>
      )}

      {noSpeech && !error && (
        <div className="px-4 py-2 text-sm text-paper-warning bg-paper-panel border-b ">
          {NO_SPEECH_MESSAGE}
        </div>
      )}

      {parsedTranscription.length > 0 ? (
        <div className="flex-1 min-h-0 relative">
          <div className="h-full overflow-y-auto px-5 py-4 text-sm text-paper-ink-2 leading-relaxed">
            <VoiceIdBar
              recognized={recognizedSpeakers}
              taskId={voiceTaskId}
              dismissKey={activeFileName || file?.name}
              onCorrect={handleEditSpeakerNames}
              suggested={suggestedSpeakers}
              onAccepted={(item) => {
                // Принятое предложение переименовывает участника в расшифровке —
                // ровно так же, как если бы человек вписал имя руками.
                handleSaveSpeakerNames({ [item.label]: item.name });
                setSuggestedSpeakers((prev) => prev.filter(
                  (s) => s.speakerId !== item.speakerId));
                setRecognizedSpeakers((prev) => [...prev, {
                  speakerId: item.speakerId, name: item.name,
                  label: item.name, confirmed: true,
                }]);
              }}
              onConfirmed={(speakerId) => {
                setRecognizedSpeakers((prev) => prev.map(
                  (item) => (item.speakerId === speakerId ? { ...item, confirmed: true } : item)));
                if (onSpeakerConfirmed) onSpeakerConfirmed(speakerId, 'transcription');
              }}
            />
            <TranscriptView
              fragments={parsedTranscription}
              recognizedLabels={recognizedSpeakers.map((item) => item.label)}
              showTimecodes={!deleteTimecodes}
            />
          </div>
          <button
            onClick={handleNewTranscription}
            className="absolute bottom-5 right-5 px-4 py-2.5 rounded-full bg-paper-accent text-white text-sm font-semibold shadow-lg shadow-violet-200 hover:bg-paper-accent-hover hover:-translate-y-0.5 transition-all"
          >
            + Новая транскрибация
          </button>
        </div>
      ) : !isProcessing && !isExtracting && (
        <div className="px-4 py-3 text-xs text-paper-hint">
          Или перетащите аудио / видео в любое место на странице
        </div>
      )}

      <EditSpeakersModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        speakers={speakers}
        onSave={handleSaveSpeakerNames}
        voiceIdEnabled={voiceIdEnabled}
        voiceIdReady={!!voiceTaskId}
      />
    </div>
  );
};

export default TranscriptionWithoutDiarization;